/**
 * Custom baseQuery wrapper for RTK Query with crash reporting
 * Intercepts 5xx errors and reports them to the crash reporting system
 */
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { reportNetworkError } from '../utils/crashReporter';
import { retryAfterSeconds } from '../utils/otpChallenge';
import { diagnosticMethod, diagnosticRoute, diagnosticStatus } from '../utils/crashPrivacy';
import { getAccessToken } from '../utils/authStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Finite category keys only: no URLs, query strings or entity IDs in memory.
const failureTracker = new Map();
const MAX_REPORTS_PER_ENDPOINT = 5; // Max 5 reports per endpoint per session
const REPORT_COOLDOWN = 60000; // 1 minute cooldown between reports for same endpoint

/**
 * Check if we should report this error
 * @param {string} endpoint - API endpoint
 * @param {number} status - HTTP status code
 * @returns {boolean} True if should report
 */
function shouldReportError(endpoint, status) {
  if (diagnosticStatus(status) === null || status < 500 || endpoint === '/crash-reports') {
    return false;
  }
  const tracker = failureTracker.get(endpoint) || { count: 0, lastReported: null };
  const now = Date.now();
  if (tracker.count >= MAX_REPORTS_PER_ENDPOINT) {
    return false;
  }
  if (tracker.lastReported !== null && now - tracker.lastReported < REPORT_COOLDOWN) {
    return false;
  }
  tracker.lastReported = now;
  tracker.count++;
  failureTracker.set(endpoint, tracker);
  return true;
}

/**
 * Create base query with crash reporting
 */
// NOTE: Global fetch is already patched in index.js to buffer every response body,
// so we no longer need a custom fetchFn here.
export const prepareAuthHeaders = (headers, { getState }) => {
  // Persistent storage is the request source of truth during partner impersonation.
  // Redux is retained as a fallback for the brief period before persistence completes.
  const token = getAccessToken() || getState().auth.token;
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
};

export const crashReportingBaseQuery = fetchBaseQuery({
  baseUrl: `${BACKEND_URL}/api`,
  credentials: 'omit',
  // RTK Query reads each response exactly once.  Use native fetch so API
  // responses are not copied into memory by the legacy global fetch wrapper.
  fetchFn: (...args) => (window.__nativeFetch || fetch)(...args),
  prepareHeaders: prepareAuthHeaders,
});

/**
 * Wrapper around base query that adds crash reporting
 */
export const baseQueryWithCrashReporting = async (args, api, extraOptions) => {
  let result;
  const endpoint = diagnosticRoute(typeof args === 'string' ? args : args?.url);
  const isAuthRequest = endpoint === '/auth';

  try {
    // Execute the base query
    result = await crashReportingBaseQuery(args, api, extraOptions);
  } catch {
    // Catch body-related errors ("Body is disturbed or locked", clone errors, etc.)
    return {
      error: {
        status: 'FETCH_ERROR',
        error: 'Network request failed',
        data: { detail: 'Network request failed. Please try again.' }
      }
    };
  }

  // Check for errors
  if (result.error) {
    const { status } = result.error;
    if (isAuthRequest && status === 429) {
      // RTK unwrap drops response metadata. Preserve only the nonsecret cooldown.
      result = { ...result, error: { ...result.error,
        retry_after_seconds: retryAfterSeconds({ response: { headers: result.meta?.response?.headers } }),
      } };
    }

    // Get endpoint information
    const method = diagnosticMethod(typeof args === 'string' ? 'GET' : args?.method);

    // Check if we should report this error
    if (shouldReportError(endpoint, status)) {
      // Report the error asynchronously (don't block the request)
      try {
        Promise.resolve(reportNetworkError({ endpoint, method, status })).catch(() => {});
      } catch { /* A reporter failure must not alter the business API result. */ }
    }
  }

  return result;
};

/**
 * Reset failure tracker (useful for testing or after user logout)
 */
export function resetFailureTracker() {
  failureTracker.clear();
}

